import React, { useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import classNames from 'classnames';
import _ from 'lodash';
import {
  FaBold,
  FaItalic,
  FaCode,
  FaListUl,
  FaListOl,
  FaFileCode,
  FaLink,
  FaUnlink,
} from 'react-icons/fa';

interface RichTextFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  className?: string;
  hasRequiredAsterisk?: boolean;
  disabled?: boolean;
}

export const RichTextField: React.FC<RichTextFieldProps> = ({
  name,
  label,
  placeholder = 'Write result notes (e.g. Actual, Expected, Notes)...',
  className,
  hasRequiredAsterisk,
  disabled = false,
}) => {
  const { control, formState } = useFormContext();

  return (
    <div className={classNames('flex flex-col w-full gap-1', className)}>
      {label && (
        <label
          htmlFor={name}
          className={classNames('font-semibold text-sm', {
            "after:content-['*'] after:text-red-400 after:text-sm": hasRequiredAsterisk,
          })}
        >
          {label}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <RichTextEditorInner
            name={name}
            value={field.value}
            onChange={field.onChange}
            placeholder={placeholder}
            disabled={disabled}
            error={_.get(formState.errors, name)?.message?.toString()}
          />
        )}
      />
    </div>
  );
};

interface RichTextEditorInnerProps {
  name: string;
  value?: string | null;
  onChange: (val: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
}

const RichTextEditorInner: React.FC<RichTextEditorInnerProps> = ({
  name,
  value,
  onChange,
  placeholder,
  disabled,
  error,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        strike: false,
        blockquote: false,
        horizontalRule: false,
        underline: false,
        link: {
          openOnClick: false,
          HTMLAttributes: {
            rel: 'noopener noreferrer',
            target: '_blank',
          },
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      if (ed.isEmpty) {
        onChange('');
      } else {
        onChange(ed.getHTML());
      }
    },
  });

  // Sync editor content when external form value changes (e.g. form reset / clear)
  useEffect(() => {
    if (!editor) return;
    const currentHTML = editor.getHTML();
    const incomingVal = value || '';

    // If incoming is empty and editor is not empty, reset it
    if (!incomingVal && !editor.isEmpty) {
      editor.commands.setContent('', { emitUpdate: false });
    } else if (incomingVal && incomingVal !== currentHTML && !editor.isFocused) {
      editor.commands.setContent(incomingVal, { emitUpdate: false });
    }
  }, [value, editor]);

  // Sync disabled state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  const handleSetLink = () => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter link URL (e.g. https://example.com):', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div
      data-testid={`richtext-${name}`}
      className={classNames(
        'border rounded-md border-gray-300 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-colors bg-white',
        {
          'border-red-500': !!error,
          'opacity-60 cursor-not-allowed bg-gray-50': disabled,
        },
      )}
    >
      {/* Toolbar: wrap-friendly for narrow drawer */}
      {editor && (
        <div className="flex flex-wrap items-center gap-1 p-1.5 border-b border-gray-200 bg-gray-50 rounded-t-md text-gray-700">
          {/* Format Group */}
          <button
            type="button"
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={classNames(
              'p-1.5 rounded hover:bg-gray-200 transition-colors',
              editor.isActive('bold') && 'bg-blue-100 text-blue-700 font-bold',
            )}
            title="Bold (Ctrl+B)"
            aria-label="Bold"
          >
            <FaBold className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={classNames(
              'p-1.5 rounded hover:bg-gray-200 transition-colors',
              editor.isActive('italic') && 'bg-blue-100 text-blue-700 italic',
            )}
            title="Italic (Ctrl+I)"
            aria-label="Italic"
          >
            <FaItalic className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleCode().run()}
            className={classNames(
              'p-1.5 rounded hover:bg-gray-200 transition-colors',
              editor.isActive('code') && 'bg-blue-100 text-blue-700',
            )}
            title="Inline Code"
            aria-label="Inline Code"
          >
            <FaCode className="w-3.5 h-3.5" />
          </button>

          <div className="w-[1px] h-4 bg-gray-300 mx-1" />

          {/* List Group */}
          <button
            type="button"
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={classNames(
              'p-1.5 rounded hover:bg-gray-200 transition-colors',
              editor.isActive('bulletList') && 'bg-blue-100 text-blue-700',
            )}
            title="Bullet List"
            aria-label="Bullet List"
          >
            <FaListUl className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={classNames(
              'p-1.5 rounded hover:bg-gray-200 transition-colors',
              editor.isActive('orderedList') && 'bg-blue-100 text-blue-700',
            )}
            title="Numbered List"
            aria-label="Numbered List"
          >
            <FaListOl className="w-3.5 h-3.5" />
          </button>

          <div className="w-[1px] h-4 bg-gray-300 mx-1" />

          {/* Block & Link Group */}
          <button
            type="button"
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            className={classNames(
              'p-1.5 rounded hover:bg-gray-200 transition-colors',
              editor.isActive('codeBlock') && 'bg-blue-100 text-blue-700',
            )}
            title="Code Block"
            aria-label="Code Block"
          >
            <FaFileCode className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={handleSetLink}
            className={classNames(
              'p-1.5 rounded hover:bg-gray-200 transition-colors',
              editor.isActive('link') && 'bg-blue-100 text-blue-700',
            )}
            title="Insert Link"
            aria-label="Insert Link"
          >
            <FaLink className="w-3.5 h-3.5" />
          </button>
          {editor.isActive('link') && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => editor.chain().focus().unsetLink().run()}
              className="p-1.5 rounded hover:bg-gray-200 transition-colors text-red-500"
              title="Remove Link"
              aria-label="Remove Link"
            >
              <FaUnlink className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Editor Content Area */}
      <div
        className="p-3 min-h-[140px] max-h-[300px] overflow-y-auto text-sm text-gray-800 cursor-text"
        onClick={() => {
          if (editor && !editor.isFocused && !disabled) {
            editor.commands.focus('end');
          }
        }}
      >
        <EditorContent
          editor={editor}
          className={classNames(
            'tiptap-editor outline-none',
            '[&_.ProseMirror]:outline-none [&_.ProseMirror:focus]:outline-none',
            '[&_.ProseMirror]:min-h-[110px]',
            '[&_.is-editor-empty:first-child::before]:text-gray-400',
            '[&_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]',
            '[&_.is-editor-empty:first-child::before]:float-left',
            '[&_.is-editor-empty:first-child::before]:pointer-events-none',
            '[&_.is-editor-empty:first-child::before]:h-0',
            '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1',
            '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1',
            '[&_code]:bg-gray-100 [&_code]:text-red-600 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono',
            '[&_pre]:bg-gray-900 [&_pre]:text-gray-100 [&_pre]:p-2.5 [&_pre]:rounded-md [&_pre]:my-2 [&_pre]:font-mono [&_pre]:text-xs',
            '[&_pre_code]:bg-transparent [&_pre_code]:text-inherit [&_pre_code]:p-0',
            '[&_a]:text-blue-600 [&_a]:underline',
          )}
        />
      </div>

      {error && (
        <div className="px-3 pb-2 text-xs text-red-500 font-medium">
          {error}
        </div>
      )}
    </div>
  );
};
